import { ChangeEvent, FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner, Stack, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  createMyScheduleRequest,
  deleteMyScheduleRequest,
  fetchMyScheduleRequests,
  updateMyScheduleRequest,
  type WorkflowRequestRecord,
  type WorkflowRequestType,
  type WorkflowShiftType,
  type WorkflowSummary,
} from '../request-workflow/api';

type RequestVisibility = 'private' | 'scheduling_admins';

type RequestFormState = {
  groupId: string;
  details: string;
  requestType: WorkflowRequestType;
  allDay: boolean;
  start: string;
  end: string;
  selectedDates: string[];
  shiftTypes: string[];
};

type RequestsSectionId = 'submit' | 'submitted' | null;
type SubmittedViewMode = 'table' | 'calendar';
type LoadState = 'loading' | 'ready' | 'error';

const emptySummary: WorkflowSummary = {
  private: 0,
  pending: 0,
  approved: 0,
  denied: 0,
  cancelled: 0,
};

const defaultFormState: RequestFormState = {
  groupId: '',
  details: '',
  requestType: 'pto',
  allDay: true,
  start: '',
  end: '',
  selectedDates: [],
  shiftTypes: [],
};

const statusBadgeClass: Record<WorkflowRequestRecord['workflowStatus'], string> = {
  private: 'bg-secondary-transparent text-secondary',
  pending: 'bg-warning-transparent text-warning',
  approved: 'bg-success-transparent text-success',
  denied: 'bg-danger-transparent text-danger',
  cancelled: 'bg-dark-transparent text-dark',
};

const requestTypeLabel: Record<WorkflowRequestType, string> = {
  pto: 'PTO',
  admin: 'Admin',
  academic: 'Academic',
  specific_shift: 'Request to work specific shifts',
  cannot_work_specific_shift: 'Cannot work specific shifts',
  swap_shift: 'Swap shifts',
  time_off: 'Time off',
  availability: 'Availability',
};

const requestTypeOptions: Array<{ value: WorkflowRequestType; label: string }> = [
  { value: 'pto', label: 'PTO' },
  { value: 'admin', label: 'Admin' },
  { value: 'academic', label: 'Academic' },
  { value: 'specific_shift', label: 'Request to work specific shifts' },
  { value: 'cannot_work_specific_shift', label: 'Cannot work specific shifts' },
  { value: 'swap_shift', label: 'Swap shifts' },
];

const specificShiftRequestTypes: WorkflowRequestType[] = ['specific_shift', 'cannot_work_specific_shift', 'swap_shift'];

const toDateInput = (value: Date) => value.toISOString().slice(0, 10);
const toDateTimeInput = (value: Date) => value.toISOString().slice(0, 16);
const startOfToday = () => toDateInput(new Date());
const toLocalIso = (value: string, allDay: boolean, boundary: 'start' | 'end') => {
  const normalized = allDay
    ? `${value}T${boundary === 'start' ? '00:00' : '23:59'}:00`
    : value;

  return new Date(normalized).toISOString();
};

const normalizeDateValue = (value: string) => value.slice(0, 10);

const dateRange = (start: string, end: string) => {
  const startDate = new Date(`${normalizeDateValue(start)}T00:00:00`);
  const endDate = new Date(`${normalizeDateValue(end)}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return [];

  const first = startDate <= endDate ? startDate : endDate;
  const last = startDate <= endDate ? endDate : startDate;
  const dates: string[] = [];
  const cursor = new Date(first);

  while (cursor <= last) {
    dates.push(toDateInput(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const mergeDates = (current: string[], incoming: string[]) =>
  Array.from(new Set([...current, ...incoming])).sort();

const toggleDate = (current: string[], value: string) =>
  current.includes(value) ? current.filter((date) => date !== value) : mergeDates(current, [value]);

const formatDateLabel = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

const formatSelectedDateSummary = (dates: string[]) => {
  const normalizedDates = Array.from(new Set(dates.map(normalizeDateValue))).sort();
  if (normalizedDates.length === 0) return null;
  if (normalizedDates.length === 1) return new Date(`${normalizedDates[0]}T00:00:00`).toLocaleDateString();

  const contiguousRange = dateRange(normalizedDates[0], normalizedDates[normalizedDates.length - 1]);
  const isContiguous = contiguousRange.length === normalizedDates.length
    && contiguousRange.every((date, index) => date === normalizedDates[index]);

  if (isContiguous) {
    return `${new Date(`${normalizedDates[0]}T00:00:00`).toLocaleDateString()} - ${new Date(`${normalizedDates[normalizedDates.length - 1]}T00:00:00`).toLocaleDateString()}`;
  }

  return normalizedDates.map((date) => formatDateLabel(date)).join(', ');
};

const getRequestDateBounds = (selectedDates: string[], fallbackStart: string, fallbackEnd: string) => {
  const normalizedDates = Array.from(new Set(selectedDates.map(normalizeDateValue))).sort();

  return {
    start: normalizedDates[0] ?? fallbackStart,
    end: normalizedDates[normalizedDates.length - 1] ?? fallbackEnd,
    selectedDates: normalizedDates,
  };
};

const formatDateRange = (record: WorkflowRequestRecord) => {
  const selectedDateSummary = record.allDay ? formatSelectedDateSummary(record.selectedDates) : null;
  if (selectedDateSummary) return selectedDateSummary;

  const start = new Date(record.start);
  const end = new Date(record.end);

  if (record.allDay) {
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  }

  return `${start.toLocaleString()} - ${end.toLocaleString()}`;
};

const formatSubmittedAt = (value: string | null) => {
  if (!value) {
    return 'Not recorded';
  }

  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const monthFormatter = new Intl.DateTimeFormat([], {
  month: 'long',
  year: 'numeric',
});

const shortDayFormatter = new Intl.DateTimeFormat([], {
  month: 'short',
  day: 'numeric',
});

const queueContextLabel = (record: WorkflowRequestRecord) => {
  if (record.visibility === 'private') {
    return 'Private only';
  }

  if (record.workflowStatus === 'pending') {
    return 'Waiting in the scheduler-admin queue';
  }

  if (record.workflowStatus === 'approved' || record.workflowStatus === 'denied') {
    return 'Reviewed in the scheduler-admin queue';
  }

  return 'No queue action required';
};

const RequestsPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const [requests, setRequests] = useState<WorkflowRequestRecord[]>([]);
  const [summary, setSummary] = useState<WorkflowSummary>(emptySummary);
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: number; name: string; slug: string; company: { slug: string | null } }>>([]);
  const [requestShiftTypes, setRequestShiftTypes] = useState<WorkflowShiftType[]>([]);
  const today = startOfToday();
  const [formState, setFormState] = useState<RequestFormState>({
    ...defaultFormState,
    start: today,
    end: today,
    selectedDates: [today],
  });
  const submitVisibilityRef = useRef<RequestVisibility>('scheduling_admins');
  const [dragSelecting, setDragSelecting] = useState(false);
  const dragSelectingRef = useRef(false);
  const [multiDateSelecting, setMultiDateSelecting] = useState(false);
  const [rangeDateSelecting, setRangeDateSelecting] = useState(false);
  const [rangeSelectionStart, setRangeSelectionStart] = useState<string | null>(null);
  const rangeSelectionStartRef = useRef<string | null>(null);
  const [rangeSelectionEnd, setRangeSelectionEnd] = useState<string | null>(null);
  const rangeSelectionEndRef = useRef<string | null>(null);
  const lastDateSelectionRef = useRef<string | null>(today);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<RequestsSectionId>(null);
  const [submittedView, setSubmittedView] = useState<SubmittedViewMode>('table');
  const [submittedCalendarMonth, setSubmittedCalendarMonth] = useState(() => startOfToday().slice(0, 7));
  const [applicableCalendarMonth, setApplicableCalendarMonth] = useState(() => startOfToday().slice(0, 7));
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const hasLoadedRequestsRef = useRef(false);

  const loadRequests = async (options?: { preserveDataOnError?: boolean }) => {
    setLoadState('loading');
    setError(null);

    try {
      const payload = await fetchMyScheduleRequests();
      setRequests(payload.requests);
      setSummary(payload.summary);
      setAvailableGroups(payload.availableGroups);
      setRequestShiftTypes(payload.requestShiftTypes);
      setFormState((current) => ({
        ...current,
        groupId: current.groupId || String(payload.availableGroups[0]?.id ?? ''),
      }));
      hasLoadedRequestsRef.current = true;
      setLoadState('ready');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load request workflow data.');
      if (options?.preserveDataOnError && hasLoadedRequestsRef.current) {
        setLoadState('ready');
        return;
      }

      hasLoadedRequestsRef.current = false;
      setRequests([]);
      setSummary(emptySummary);
      setAvailableGroups([]);
      setRequestShiftTypes([]);
      setLoadState('error');
    }
  };

  useEffect(() => {
    void loadRequests();
  }, []);

  const handleChange = (field: keyof RequestFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = event.target instanceof HTMLInputElement && event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

      if (field === 'start') {
        setApplicableCalendarMonth(normalizeDateValue(String(value)).slice(0, 7));
      }

      setFormState((current) => {
        if (field === 'start') {
          const nextStart = String(value);
          const nextEnd = new Date(`${normalizeDateValue(nextStart)}T00:00:00`) > new Date(`${normalizeDateValue(current.end)}T00:00:00`)
            ? nextStart
            : current.end;

          return {
            ...current,
            start: nextStart,
            end: nextEnd,
            selectedDates: current.allDay ? dateRange(nextStart, nextEnd) : current.selectedDates,
          };
        }

        if (field === 'end') {
          const nextEnd = String(value);
          return {
            ...current,
            end: nextEnd,
            selectedDates: current.allDay ? dateRange(current.start, nextEnd) : current.selectedDates,
          };
        }

        if (field === 'requestType') {
          const nextRequestType = String(value) as WorkflowRequestType;

          return {
            ...current,
            requestType: nextRequestType,
            shiftTypes: specificShiftRequestTypes.includes(nextRequestType) ? current.shiftTypes : [],
          };
        }

        if (field === 'allDay') {
          const nextAllDay = Boolean(value);
          const nextStart = nextAllDay ? normalizeDateValue(current.start) : `${normalizeDateValue(current.start)}T07:00`;
          const nextEnd = nextAllDay ? normalizeDateValue(current.end) : `${normalizeDateValue(current.end)}T19:00`;

          return {
            ...current,
            allDay: nextAllDay,
            start: nextStart,
            end: nextEnd,
            selectedDates: nextAllDay ? dateRange(nextStart, nextEnd) : current.selectedDates,
          };
        }

        return {
          ...current,
          [field]: value,
        };
      });
    };

  const selectDate = (date: string, allowMultiple = false) => {
    lastDateSelectionRef.current = date;
    setFormState((current) => ({
      ...current,
      selectedDates: allowMultiple ? toggleDate(current.selectedDates, date) : [date],
    }));
  };

  const selectShiftRange = (date: string) => {
    const rangeStart = lastDateSelectionRef.current ?? formState.selectedDates[0] ?? date;
    lastDateSelectionRef.current = date;
    setFormState((current) => ({
      ...current,
      selectedDates: dateRange(rangeStart, date),
    }));
  };

  const selectRangeStart = (date: string) => {
    rangeSelectionStartRef.current = date;
    rangeSelectionEndRef.current = date;
    setRangeSelectionStart(date);
    setRangeSelectionEnd(date);
    setFormState((current) => ({
      ...current,
      selectedDates: [date],
    }));
  };

  const selectRangeThrough = (date: string, complete = false) => {
    const activeRangeStart = rangeSelectionStartRef.current ?? rangeSelectionStart;

    if (!activeRangeStart) {
      selectRangeStart(date);
      return;
    }

    rangeSelectionEndRef.current = date;
    setRangeSelectionEnd(date);
    setFormState((current) => ({
      ...current,
      selectedDates: dateRange(activeRangeStart, date),
    }));

    if (complete) {
      lastDateSelectionRef.current = date;
      rangeSelectionStartRef.current = null;
      rangeSelectionEndRef.current = null;
      setRangeSelectionStart(null);
      setRangeSelectionEnd(null);
      setRangeDateSelecting(false);
    }
  };

  const toggleRangeDateSelecting = () => {
    setRangeDateSelecting((current) => {
      const next = !current;
      if (next) setMultiDateSelecting(false);
      rangeSelectionStartRef.current = null;
      rangeSelectionEndRef.current = null;
      setRangeSelectionStart(null);
      setRangeSelectionEnd(null);
      return next;
    });
  };

  const finishDateDrag = () => {
    const activeRangeStart = rangeSelectionStartRef.current ?? rangeSelectionStart;
    const activeRangeEnd = rangeSelectionEndRef.current ?? rangeSelectionEnd;

    if (rangeDateSelecting && dragSelectingRef.current && activeRangeStart && activeRangeEnd && activeRangeEnd !== activeRangeStart) {
      rangeSelectionStartRef.current = null;
      rangeSelectionEndRef.current = null;
      setRangeSelectionStart(null);
      setRangeSelectionEnd(null);
      setRangeDateSelecting(false);
    }

    dragSelectingRef.current = false;
    setDragSelecting(false);
  };

  const clearSelectedDates = () => {
    lastDateSelectionRef.current = null;
    setFormState((current) => ({
      ...current,
      selectedDates: [],
    }));
  };

  const toggleShiftType = (shiftType: string) => {
    setFormState((current) => ({
      ...current,
      shiftTypes: current.shiftTypes.includes(shiftType)
        ? current.shiftTypes.filter((item) => item !== shiftType)
        : [...current.shiftTypes, shiftType],
    }));
  };

  const groupedRequests = useMemo(
    () => requests.slice().sort((left, right) => {
      const leftDate = new Date(left.submittedAt ?? left.start).getTime();
      const rightDate = new Date(right.submittedAt ?? right.start).getTime();

      return rightDate - leftDate;
    }),
    [requests]
  );

  const activeSubmittedRequests = useMemo(() => {
    const todayValue = startOfToday();

    return groupedRequests.filter((record) => {
      const requestedDates = record.selectedDates.length > 0
        ? record.selectedDates.map(normalizeDateValue)
        : dateRange(record.start, record.end);
      const latestRequestedDate = requestedDates.sort().at(-1) ?? normalizeDateValue(record.end);

      return ['pending', 'approved'].includes(record.workflowStatus) && latestRequestedDate >= todayValue;
    });
  }, [groupedRequests]);

  const hiddenCancelledPrivateRequests = useMemo(
    () => groupedRequests.filter((record) => record.visibility === 'private' && record.workflowStatus === 'cancelled'),
    [groupedRequests]
  );

  const privateSubmittedRequests = useMemo(
    () => groupedRequests.filter((record) => (record.workflowStatus === 'private' || record.visibility === 'private') && record.workflowStatus !== 'cancelled'),
    [groupedRequests]
  );

  const archivedSubmittedRequests = useMemo(
    () => groupedRequests.filter((record) => {
      const isActive = activeSubmittedRequests.some((activeRecord) => activeRecord.id === record.id);
      const isPrivate = privateSubmittedRequests.some((privateRecord) => privateRecord.id === record.id);
      const isHiddenCancelledPrivate = hiddenCancelledPrivateRequests.some((privateRecord) => privateRecord.id === record.id);

      return !isActive && !isPrivate && !isHiddenCancelledPrivate;
    }),
    [activeSubmittedRequests, groupedRequests, hiddenCancelledPrivateRequests, privateSubmittedRequests]
  );

  const submittedCalendarLabel = useMemo(
    () => monthFormatter.format(new Date(`${submittedCalendarMonth}-01T00:00:00`)),
    [submittedCalendarMonth]
  );

  const submittedCalendarDays = useMemo(() => {
    const base = new Date(`${submittedCalendarMonth}-01T00:00:00`);
    if (Number.isNaN(base.getTime())) return [];

    const firstOfMonth = new Date(base.getFullYear(), base.getMonth(), 1);
    const firstVisible = new Date(firstOfMonth);
    firstVisible.setDate(firstVisible.getDate() - firstVisible.getDay());
    const recordsByDate = new Map<string, WorkflowRequestRecord[]>();

    activeSubmittedRequests.forEach((record) => {
      const dates = record.selectedDates.length > 0
        ? Array.from(new Set(record.selectedDates.map(normalizeDateValue))).sort()
        : dateRange(record.start, record.end);

      dates.forEach((date) => {
        const existing = recordsByDate.get(date) ?? [];
        existing.push(record);
        recordsByDate.set(date, existing);
      });
    });

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(firstVisible);
      date.setDate(firstVisible.getDate() + index);
      const value = toDateInput(date);

      return {
        value,
        label: String(date.getDate()),
        muted: date.getMonth() !== base.getMonth(),
        records: recordsByDate.get(value) ?? [],
      };
    });
  }, [activeSubmittedRequests, submittedCalendarMonth]);

  const moveSubmittedCalendarMonth = (offset: number) => {
    setSubmittedCalendarMonth((current) => {
      const date = new Date(`${current}-01T00:00:00`);
      date.setMonth(date.getMonth() + offset);
      return toDateInput(date).slice(0, 7);
    });
  };

  const applicableCalendarLabel = useMemo(
    () => monthFormatter.format(new Date(`${applicableCalendarMonth}-01T00:00:00`)),
    [applicableCalendarMonth]
  );

  const selectableDates = useMemo(() => {
    const base = new Date(`${applicableCalendarMonth}-01T00:00:00`);
    if (Number.isNaN(base.getTime())) return [];

    const firstOfMonth = new Date(base.getFullYear(), base.getMonth(), 1);
    const firstVisible = new Date(firstOfMonth);
    firstVisible.setDate(firstVisible.getDate() - firstVisible.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(firstVisible);
      date.setDate(firstVisible.getDate() + index);
      const value = toDateInput(date);

      return {
        value,
        label: String(date.getDate()),
        muted: date.getMonth() !== base.getMonth(),
      };
    });
  }, [applicableCalendarMonth]);

  const moveApplicableCalendarMonth = (offset: number) => {
    const currentMonth = startOfToday().slice(0, 7);

    setApplicableCalendarMonth((current) => {
      const date = new Date(`${current}-01T00:00:00`);
      date.setMonth(date.getMonth() + offset);
      const nextMonth = toDateInput(date).slice(0, 7);

      return nextMonth < currentMonth ? currentMonth : nextMonth;
    });
  };

  const buildRequestPayload = (visibility: RequestVisibility) => {
    const selectedDates = formState.selectedDates.length > 0
      ? formState.selectedDates
      : dateRange(formState.start, formState.end);
    const dateBounds = formState.allDay
      ? getRequestDateBounds(selectedDates, formState.start, formState.end)
      : {
          start: formState.start,
          end: formState.end,
          selectedDates: Array.from(new Set(selectedDates.map(normalizeDateValue))).sort(),
        };

    return {
      group_id: Number(formState.groupId),
      details: formState.details.trim() || undefined,
      start: toLocalIso(dateBounds.start, formState.allDay, 'start'),
      end: toLocalIso(dateBounds.end, formState.allDay, 'end'),
      allDay: formState.allDay,
      visibility,
      request_type: formState.requestType,
      selected_dates: dateBounds.selectedDates,
      shift_types: specificShiftRequestTypes.includes(formState.requestType) ? formState.shiftTypes : [],
    };
  };

  const resetRequestForm = (groupId = formState.groupId) => {
    const nextToday = startOfToday();
    setEditingRequestId(null);
    setApplicableCalendarMonth(nextToday.slice(0, 7));
    setFormState({
      ...defaultFormState,
      start: nextToday,
      end: nextToday,
      selectedDates: [nextToday],
      groupId,
    });
  };

  const requestActionDisabledReason = (record: WorkflowRequestRecord) => {
    if (!['private', 'pending'].includes(record.workflowStatus)) return 'Only private requests or requests still pending approval can be changed or deleted.';

    // Scheduler-admin lockout rules are TBD. When schedule draft/published lockout settings are available,
    // return that reason here so the buttons render greyed out before the backend also rejects the action.
    return null;
  };

  const editSubmittedRequest = (record: WorkflowRequestRecord) => {
    const disabledReason = requestActionDisabledReason(record);
    if (disabledReason) return;

    const selectedDates = record.selectedDates.length > 0
      ? record.selectedDates.map(normalizeDateValue)
      : dateRange(record.start, record.end);

    setEditingRequestId(record.id);
    setApplicableCalendarMonth(normalizeDateValue(record.start).slice(0, 7));
    setFormState({
      groupId: String(record.group.id),
      details: record.details ?? '',
      requestType: record.requestType,
      allDay: record.allDay,
      start: record.allDay ? normalizeDateValue(record.start) : record.start.slice(0, 16),
      end: record.allDay ? normalizeDateValue(record.end) : record.end.slice(0, 16),
      selectedDates,
      shiftTypes: record.shiftTypes,
    });
    setActiveSection('submit');
    setSuccess(record.visibility === 'private'
      ? 'Editing a private request. Save changes when the draft is ready.'
      : 'Editing a pending request. Save changes to update the scheduler-admin queue.');
    setError(null);
  };

  const deleteSubmittedRequest = async (record: WorkflowRequestRecord) => {
    const disabledReason = requestActionDisabledReason(record);
    if (disabledReason) return;
    if (!window.confirm(record.visibility === 'private'
      ? 'Delete this private request?'
      : 'Delete this pending request from the scheduler-admin queue?')) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await deleteMyScheduleRequest(record.id);
      if (editingRequestId === record.id) resetRequestForm();
      setSuccess(record.visibility === 'private'
        ? 'Private request deleted.'
        : 'Pending request deleted from the scheduler-admin queue.');
      await loadRequests({ preserveDataOnError: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to delete this request.');
    } finally {
      setSaving(false);
    }
  };

  const saveRequest = async (visibility: RequestVisibility) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = buildRequestPayload(visibility);

      if (editingRequestId) {
        await updateMyScheduleRequest(editingRequestId, payload);
        setSuccess(payload.visibility === 'private'
          ? 'Request saved privately.'
          : 'Request updated and sent to the scheduler-admin queue.');
      } else {
        await createMyScheduleRequest(payload);
        setSuccess(payload.visibility === 'private'
          ? 'Request saved privately.'
          : 'Request submitted to the scoped scheduling-admin queue.');
      }

      if (payload.visibility === 'private') {
        setActiveSection('submit');
      } else {
        setActiveSection('submitted');
        setSubmittedView('table');
      }
      resetRequestForm(formState.groupId);
      submitVisibilityRef.current = 'scheduling_admins';
      await loadRequests({ preserveDataOnError: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save this request.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const visibility = submitVisibilityRef.current;
    submitVisibilityRef.current = 'scheduling_admins';
    void saveRequest(visibility);
  };

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="My Requests" actions={<></>} />
        <Alert variant="danger" className="custom-card">
          This request workspace requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to manage your scheduling requests.
        </Alert>
      </>
    );
  }

  if (loadState === 'error') {
    return (
      <>
        <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="My Requests" actions={<></>} />
        <Card className="custom-card">
          <Card.Body className="p-4 p-xl-5">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
              <div>
                <Badge bg="" className="bg-danger-transparent text-danger mb-3">
                  Workspace unavailable
                </Badge>
                <h2 className="mb-2">My Requests could not load.</h2>
                <p className="text-muted mb-0" style={{ maxWidth: '42rem' }}>
                  The page could not load your request history, summary totals, or eligible departments. Retry the full load when the
                  scheduling service is available again.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button type="button" variant="primary" onClick={() => void loadRequests()}>
                  Retry
                </Button>
                <Link to="/my-schedule" className="btn btn-light">
                  Return to Overview
                </Link>
              </div>
            </div>
            {error ? <Alert variant="danger" className="mb-0 mt-4">{error}</Alert> : null}
          </Card.Body>
        </Card>
      </>
    );
  }

  const showInitialLoadingState = loadState === 'loading' && !hasLoadedRequestsRef.current;

  const submitRequestCard = (
    <Card className="custom-card h-100">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <Card.Title>{editingRequestId ? 'Change submitted request' : 'Submit a request'}</Card.Title>
        {editingRequestId ? (
          <Button type="button" size="sm" variant="light" className="border" onClick={() => resetRequestForm()}>
            Cancel edit
          </Button>
        ) : null}
      </Card.Header>
      <Card.Body>
        {showInitialLoadingState ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            Loading your departments.
          </div>
        ) : availableGroups.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No active departments are currently available for this account.
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>Team</Form.Label>
                <Form.Select value={formState.groupId} onChange={handleChange('groupId')} required>
                  {availableGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.company.slug ?? 'company'})
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label>Request type</Form.Label>
                <Form.Select value={formState.requestType} onChange={handleChange('requestType')}>
                  {requestTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Check
                  id="request-all-day"
                  type="switch"
                  label="All-day request"
                  checked={formState.allDay}
                  onChange={handleChange('allDay')}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Start date</Form.Label>
                <Form.Control
                  type={formState.allDay ? 'date' : 'datetime-local'}
                  value={formState.start}
                  onChange={handleChange('start')}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>End date</Form.Label>
                <Form.Control
                  type={formState.allDay ? 'date' : 'datetime-local'}
                  value={formState.end}
                  onChange={handleChange('end')}
                  min={normalizeDateValue(formState.start)}
                  required
                />
              </Col>
              {specificShiftRequestTypes.includes(formState.requestType) ? (
                <Col md={12}>
                  <Form.Label>Eligible shifts</Form.Label>
                  <Form.Select
                    multiple
                    value={formState.shiftTypes}
                    onChange={(event) => {
                      const values = Array.from(event.currentTarget.selectedOptions).map((option) => option.value);
                      setFormState((current) => ({
                        ...current,
                        shiftTypes: values,
                      }));
                    }}
                    style={{ minHeight: '8rem' }}
                  >
                    {requestShiftTypes.map((shiftType) => (
                      <option key={shiftType.key} value={shiftType.key}>
                        {shiftType.label}
                      </option>
                    ))}
                  </Form.Select>
                  <div className="text-muted fs-12 mt-2">
                    Multi-select the shifts the user is eligible to work for this request.
                  </div>
                </Col>
              ) : null}
              <Col md={12}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div>
                    <Form.Label className="mb-0">Applicable dates</Form.Label>
                    <div className="text-muted fs-12">{applicableCalendarLabel}</div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="light"
                      className="border"
                      disabled={applicableCalendarMonth <= startOfToday().slice(0, 7)}
                      onClick={() => moveApplicableCalendarMonth(-1)}
                    >
                      Previous month
                    </Button>
                    <Button type="button" size="sm" variant="light" className="border" onClick={() => setApplicableCalendarMonth(startOfToday().slice(0, 7))}>
                      Current month
                    </Button>
                    <Button type="button" size="sm" variant="light" className="border" onClick={() => moveApplicableCalendarMonth(1)}>
                      Next month
                    </Button>
                    <Button type="button" size="sm" variant={rangeDateSelecting ? 'primary' : 'light'} className="border" onClick={toggleRangeDateSelecting}>
                      Add start-end range
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={multiDateSelecting ? 'primary' : 'light'}
                      className="border"
                      onClick={() => {
                        setMultiDateSelecting((current) => !current);
                        setRangeDateSelecting(false);
                        rangeSelectionStartRef.current = null;
                        setRangeSelectionStart(null);
                        setRangeSelectionEnd(null);
                      }}
                    >
                      Select multiple dates
                    </Button>
                    <Button type="button" size="sm" variant="light" className="border" onClick={clearSelectedDates}>
                      Clear dates
                    </Button>
                  </div>
                </div>
                <div
                  className="border rounded p-2"
                  onMouseLeave={finishDateDrag}
                  onMouseUp={finishDateDrag}
                >
                  <div className="d-grid gap-1" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-muted text-center fs-12 fw-semibold py-1">
                        {day}
                      </div>
                    ))}
                    {selectableDates.map((date) => {
                      const selected = formState.selectedDates.includes(date.value);

                      return (
                        <Button
                          key={date.value}
                          type="button"
                          size="sm"
                          variant={selected ? 'primary' : 'light'}
                          className={`border ${date.muted ? 'text-muted opacity-75' : ''}`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            if (rangeDateSelecting) {
                              const activeRangeStart = rangeSelectionStartRef.current ?? rangeSelectionStart;
                              dragSelectingRef.current = true;
                              setDragSelecting(true);
                              if (activeRangeStart && activeRangeStart !== date.value) {
                                selectRangeThrough(date.value, true);
                              } else {
                                selectRangeStart(date.value);
                              }
                              return;
                            }

                            if (event.shiftKey) {
                              dragSelectingRef.current = false;
                              setDragSelecting(false);
                              selectShiftRange(date.value);
                              return;
                            }

                            const allowMultiple = multiDateSelecting || event.ctrlKey || event.metaKey;
                            dragSelectingRef.current = allowMultiple;
                            setDragSelecting(allowMultiple);
                            selectDate(date.value, allowMultiple);
                          }}
                          onMouseEnter={() => {
                            if (rangeDateSelecting && dragSelectingRef.current) {
                              selectRangeThrough(date.value);
                              return;
                            }

                            if (dragSelectingRef.current && !formState.selectedDates.includes(date.value)) selectDate(date.value, true);
                          }}
                          onTouchStart={() => {
                            if (rangeDateSelecting) {
                              if (rangeSelectionStart) selectRangeThrough(date.value, true);
                              else selectRangeStart(date.value);
                              return;
                            }

                            selectDate(date.value, multiDateSelecting);
                          }}
                        >
                          {date.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                {rangeDateSelecting ? (
                  <div className="text-primary fs-12 mt-2">
                    {rangeSelectionStart ? 'Select the end date for this range.' : 'Select the start date for this range.'}
                  </div>
                ) : null}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {formState.selectedDates.length === 0 ? (
                    <span className="text-muted fs-12">No specific dates selected; the start/end range will be used.</span>
                  ) : formState.selectedDates.map((date) => (
                    <Badge key={date} bg="" className="bg-primary-transparent text-primary">
                      {formatDateLabel(date)}
                    </Badge>
                  ))}
                </div>
              </Col>
              <Col md={12}>
                <Form.Label>Notes for the queue</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={formState.details}
                  onChange={handleChange('details')}
                  placeholder="Why you need this change, coverage notes, or handoff details."
                />
              </Col>
              <Col md={12}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Button
                    type="submit"
                    disabled={saving || !formState.groupId}
                    onClick={() => {
                      submitVisibilityRef.current = 'scheduling_admins';
                    }}
                  >
                    {saving ? 'Saving...' : editingRequestId ? 'Submit request changes' : 'Submit request'}
                  </Button>
                  <Button
                    type="submit"
                    variant="light"
                    className="border"
                    disabled={saving || !formState.groupId}
                    onClick={() => {
                      submitVisibilityRef.current = 'private';
                    }}
                  >
                    Save as private request
                  </Button>
                </div>
                <div className="text-muted fs-12 mt-2">
                  Private requests are not submitted to the scheduler admin for review.
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Card.Body>
    </Card>
  );

  const requestWorkflowNotesCard = (
    <Card className="custom-card h-100">
      <Card.Header>
        <Card.Title>Request workflow notes</Card.Title>
      </Card.Header>
      <Card.Body>
        <Stack gap={3}>
          <Alert variant="light" className="mb-0">
            Requests now go straight to the scheduling-admin queue for the selected team.
          </Alert>
          <Alert variant="light" className="mb-0">
            For full-day requests, pick one date range or multi-select individual dates. For partial-day requests,
            choose the shift types the user wants off.
          </Alert>
          <Card className="border shadow-none mb-0">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between gap-3">
                <div>
                  <p className="text-uppercase text-muted fs-12 fw-semibold mb-1">Next section</p>
                  <h4 className="mb-1">Request Policy lives on its own read-only subpage.</h4>
                  <p className="text-muted mb-0">
                    Open the separate policy reference for the current submission, review, and request-history rules without leaving
                    the My Requests workflow.
                  </p>
                </div>
                <Link to="/my-schedule/requests/policy" className="btn btn-light btn-wave border align-self-start">
                  Open Request Policy
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Stack>
      </Card.Body>
    </Card>
  );

  const renderSubmittedRequestsTable = (records: WorkflowRequestRecord[]) => (
    <Table responsive hover className="align-middle mb-0">
      <thead>
        <tr>
          <th>Status</th>
          <th>Requested dates</th>
          <th>Reason for request</th>
          <th>Date of submission</th>
          <th>Queue context</th>
          <th>Options</th>
        </tr>
      </thead>
      <tbody>
        {records.map((record) => {
          const disabledReason = requestActionDisabledReason(record);

          return (
          <tr key={record.id}>
            <td>
              <Badge bg="" className={statusBadgeClass[record.workflowStatus]}>
                {record.workflowStatus.replace('_', ' ')}
              </Badge>
              {record.decision ? (
                <div className="text-muted fs-12 mt-1">
                  {record.decision.status === 'approved' ? 'Approved' : 'Denied'}
                  {record.decision.decidedByName ? ` by ${record.decision.decidedByName}` : ''}
                </div>
              ) : null}
            </td>
            <td>{formatDateRange(record)}</td>
            <td>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span className="fw-semibold">{record.title}</span>
                <Badge bg="" className="bg-light text-muted border">
                  {requestTypeLabel[record.requestType]}
                </Badge>
              </div>
              {record.details ? (
                <div className="text-muted fs-13" style={{ maxWidth: '22rem', whiteSpace: 'normal' }}>
                  {record.details}
                </div>
              ) : null}
            </td>
            <td>
              <div className="fw-semibold">{formatSubmittedAt(record.submittedAt)}</div>
              <div className="text-muted fs-12">
                {record.allDay ? 'All day' : 'Timed request'}
              </div>
            </td>
            <td>
              <div>{queueContextLabel(record)}</div>
              {record.decision?.note ? (
                <div className="text-muted fs-12 mt-1" style={{ whiteSpace: 'normal' }}>
                  {record.decision.note}
                </div>
              ) : null}
            </td>
            <td>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="border"
                  disabled={Boolean(disabledReason) || saving}
                  title={disabledReason ?? 'Change this pending request'}
                  onClick={() => editSubmittedRequest(record)}
                >
                  Change
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="border text-danger"
                  disabled={Boolean(disabledReason) || saving}
                  title={disabledReason ?? 'Delete this pending request'}
                  onClick={() => void deleteSubmittedRequest(record)}
                >
                  Delete
                </Button>
              </div>
              {disabledReason ? (
                <div className="text-muted fs-12 mt-1" style={{ maxWidth: '13rem', whiteSpace: 'normal' }}>
                  {disabledReason}
                </div>
              ) : null}
            </td>
          </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const submittedRequestsCard = (
    <Card className="custom-card">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <Card.Title className="mb-1">My Submitted Requests</Card.Title>
          <p className="text-muted mb-0">
            Review prior and future requests, the dates they cover, and whether they stayed private or entered the
            review queue.
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          {loadState === 'loading' ? <Spinner animation="border" size="sm" /> : null}
          <ButtonGroup size="sm" aria-label="Submitted request views">
            <Button
              variant={submittedView === 'table' ? 'primary' : 'light'}
              onClick={() => setSubmittedView('table')}
            >
              Table
            </Button>
            <Button
              variant={submittedView === 'calendar' ? 'primary' : 'light'}
              onClick={() => setSubmittedView('calendar')}
            >
              Calendar
            </Button>
          </ButtonGroup>
        </div>
      </Card.Header>
      <Card.Body>
        {showInitialLoadingState ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            Loading your requests.
          </div>
        ) : groupedRequests.length === 0 ? (
          <Alert variant="light" className="mb-0">
            No request records yet. Submit your first request to open the submitted-requests view.
          </Alert>
        ) : submittedView === 'table' ? (
          <Stack gap={4}>
            <div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div>
                  <h4 className="mb-1">Active requests</h4>
                  <p className="text-muted mb-0">Pending and approved requests with future requested dates.</p>
                </div>
                <Badge bg="" className="bg-primary-transparent text-primary">
                  {activeSubmittedRequests.length}
                </Badge>
              </div>
              {activeSubmittedRequests.length > 0 ? renderSubmittedRequestsTable(activeSubmittedRequests) : (
                <Alert variant="light" className="mb-0">
                  No pending or approved future requests.
                </Alert>
              )}
            </div>
            <div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div>
                  <h4 className="mb-1">Private requests</h4>
                  <p className="text-muted mb-0">Requests saved for the end user only and not sent to scheduler review.</p>
                </div>
                <Badge bg="" className="bg-secondary-transparent text-secondary">
                  {privateSubmittedRequests.length}
                </Badge>
              </div>
              {privateSubmittedRequests.length > 0 ? renderSubmittedRequestsTable(privateSubmittedRequests) : (
                <Alert variant="light" className="mb-0">
                  No private requests yet.
                </Alert>
              )}
            </div>
            <div>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div>
                  <h4 className="mb-1">Archived requests</h4>
                  <p className="text-muted mb-0">Prior requests.</p>
                </div>
                <Badge bg="" className="bg-light text-muted border">
                  {archivedSubmittedRequests.length}
                </Badge>
              </div>
              {archivedSubmittedRequests.length > 0 ? renderSubmittedRequestsTable(archivedSubmittedRequests) : (
                <Alert variant="light" className="mb-0">
                  No prior requests yet.
                </Alert>
              )}
            </div>
          </Stack>
        ) : (
          <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
              <div>
                <h4 className="mb-1">{submittedCalendarLabel}</h4>
                <p className="text-muted mb-0">Month calendar view of active pending and approved request dates.</p>
              </div>
              <ButtonGroup size="sm" aria-label="Submitted request calendar month navigation">
                <Button variant="light" className="border" onClick={() => moveSubmittedCalendarMonth(-1)}>
                  Previous
                </Button>
                <Button variant="light" className="border" onClick={() => setSubmittedCalendarMonth(startOfToday().slice(0, 7))}>
                  Today
                </Button>
                <Button variant="light" className="border" onClick={() => moveSubmittedCalendarMonth(1)}>
                  Next
                </Button>
              </ButtonGroup>
            </div>
            <div className="d-grid gap-1" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="text-muted text-center fs-12 fw-semibold py-1">
                  {day}
                </div>
              ))}
              {submittedCalendarDays.map((day) => (
                <div
                  key={day.value}
                  className={`border rounded p-2 bg-white ${day.muted ? 'text-muted opacity-75' : ''}`}
                  style={{ minHeight: '7.5rem' }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    <span className="fw-semibold">{day.label}</span>
                    {day.records.length > 0 ? (
                      <Badge bg="" className="bg-primary-transparent text-primary">
                        {day.records.length}
                      </Badge>
                    ) : null}
                  </div>
                  <Stack gap={1}>
                    {day.records.slice(0, 3).map((record) => (
                      <div key={`${day.value}-${record.id}`} className="border rounded p-1 bg-light">
                        <div className="d-flex align-items-center gap-1 mb-1">
                          <Badge bg="" className={statusBadgeClass[record.workflowStatus]}>
                            {record.workflowStatus.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="fw-semibold text-truncate fs-12">{record.title}</div>
                        <div className="text-muted text-truncate fs-12">{requestTypeLabel[record.requestType]}</div>
                      </div>
                    ))}
                    {day.records.length > 3 ? (
                      <span className="text-muted fs-12">+{day.records.length - 3} more</span>
                    ) : null}
                  </Stack>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <>
      <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="My Requests" actions={<></>} />

      <Card className="custom-card cares-page-hero">
        <Card.Body className="p-3 p-xl-4">
          <Row className="g-3 align-items-start">
            <Col xl={8}>
              <Badge bg="" className="bg-primary-transparent text-primary mb-3">
                My Requests workspace
              </Badge>
              <h2 className="mb-2">My Requests</h2>
              <p className="text-muted mb-0" style={{ maxWidth: '52rem' }}>
                Submit new requests and view status of submitted requests.
              </p>
            </Col>
            <Col xl={4}>
              <Row className="g-2">
                <Col sm={6} xl={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Pending review</p>
                      <h3 className="mb-0">{summary.pending}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Approved</p>
                      <h3 className="mb-0">{summary.approved}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Private</p>
                      <h3 className="mb-0">{summary.private}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} xl={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Denied</p>
                      <h3 className="mb-0">{summary.denied}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button
              variant={activeSection === 'submit' ? 'primary' : 'light'}
              className="border"
              onClick={() => setActiveSection((current) => (current === 'submit' ? null : 'submit'))}
            >
              Submit a Request
            </Button>
            <Button
              variant={activeSection === 'submitted' ? 'primary' : 'light'}
              className="border"
              onClick={() => setActiveSection((current) => (current === 'submitted' ? null : 'submitted'))}
            >
              My Submitted Requests
            </Button>
            <Link to="/my-schedule/requests/policy" className="btn btn-light btn-wave border">
              Request Policy
            </Link>
          </div>
        </Card.Body>
      </Card>

      {error ? (
        <Alert variant="warning" className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <span>{error}</span>
          <Button type="button" variant="outline-warning" size="sm" onClick={() => void loadRequests({ preserveDataOnError: true })}>
            Retry full load
          </Button>
        </Alert>
      ) : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Row className="g-4">
        {activeSection !== 'submitted' ? (
          <Col xl={activeSection === 'submit' ? 12 : 8}>
            {submitRequestCard}
          </Col>
        ) : null}
        {activeSection !== 'submit' ? (
          <Col xl={activeSection === 'submitted' ? 12 : 4}>
            {submittedRequestsCard}
          </Col>
        ) : null}
        <Col xl={12}>
          {requestWorkflowNotesCard}
        </Col>
      </Row>
    </>
  );
};

export default RequestsPage;
